import { useEffect, useState } from 'react';

import type { Entity } from '@backstage/catalog-model';
import {
  DefaultEntityFilters,
  EntityAutocompletePicker,
  EntityFilter,
  EntityOwnerPicker,
  useEntityList,
  useEntityOwnership,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';

import { useStyles } from './styles';

export class UserFilter implements EntityFilter {
  constructor(
    readonly showStarred: boolean,
    readonly showOwned: boolean,
    private readonly isOwnedFn: (entity: Entity) => boolean,
    private readonly isStarredFn: (entity: Entity) => boolean,
  ) {}

  filterEntity(entity: Entity): boolean {
    if (!this.showStarred && !this.showOwned) return true;
    if (this.showStarred && this.showOwned) {
      return this.isStarredFn(entity) && this.isOwnedFn(entity);
    }
    return (
      (this.showStarred && this.isStarredFn(entity)) ||
      (this.showOwned && this.isOwnedFn(entity))
    );
  }

  toQueryValue(): string[] {
    const parts: string[] = [];
    if (this.showStarred) parts.push('starred');
    if (this.showOwned) parts.push('owned');
    return parts;
  }
}

export class DaaSProjectFilter implements EntityFilter {
  constructor(readonly values: string[]) {}

  getCatalogFilters(): Record<string, string | string[]> {
    return { 'spec.projectName': this.values };
  }

  toQueryValue(): string[] {
    return this.values;
  }
}

export type DaaSEntityFilters = DefaultEntityFilters & {
  daasUser?: UserFilter;
  daasProject?: DaaSProjectFilter;
};

export const DaaSQuickFilters = () => {
  const { classes } = useStyles();
  const { isOwnedEntity } = useEntityOwnership();
  const { isStarredEntity } = useStarredEntities();

  const {
    updateFilters,
    queryParameters: { daasUser: daasUserParam },
  } = useEntityList<DaaSEntityFilters>();

  // Seeded from the query string so a saved URL restores the filters. Owned by
  // the component after the first render.
  const [initialUserFilter] = useState(
    () => [daasUserParam].flat().filter(Boolean) as string[],
  );

  const [showStarred, setShowStarred] = useState(
    initialUserFilter.includes('starred'),
  );
  const [showOwned, setShowOwned] = useState(
    initialUserFilter.includes('owned'),
  );

  useEffect(() => {
    updateFilters({
      users: undefined,
      daasUser:
        showStarred || showOwned
          ? new UserFilter(
              showStarred,
              showOwned,
              isOwnedEntity,
              isStarredEntity,
            )
          : undefined,
    } as Partial<DaaSEntityFilters>);
  }, [showStarred, showOwned, isOwnedEntity, isStarredEntity, updateFilters]);

  return (
    <Box marginBottom={1}>
      <Typography className={classes.filtersTitle}>Filters</Typography>
      <Box className={classes.filtersCheckboxRoot}>
        <FormControlLabel
          control={
            <Checkbox
              size="medium"
              checked={showStarred}
              onChange={(_, checked) => setShowStarred(checked)}
              color="primary"
            />
          }
          label={
            <Box className={classes.filtersCheckboxLabel}>
              <StarIcon />
              <span>Starred</span>
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              size="medium"
              checked={showOwned}
              onChange={(_, checked) => setShowOwned(checked)}
              color="primary"
            />
          }
          label={
            <Box className={classes.filtersCheckboxLabel}>
              <PersonIcon />
              <span>Owned by me</span>
            </Box>
          }
        />
      </Box>
    </Box>
  );
};

export const DaaSFilters = () => {
  const { classes } = useStyles();

  return (
    <Box
      component="section"
      aria-label="DaaS filters"
      className={classes.filtersContainer}
    >
      <Box>
        <DaaSQuickFilters />
        <EntityOwnerPicker />
        <EntityAutocompletePicker<DaaSEntityFilters>
          label="Project"
          name="daasProject"
          path="spec.projectName"
          Filter={DaaSProjectFilter}
        />
      </Box>
    </Box>
  );
};
